import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { logAccessServer } from '@/lib/accessLogs';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';

interface ProfileData {
  id: string;
  nom: string;
  prenom: string;
  email?: string;
  telephone?: string;
  date_naissance?: string;
  name_changes?: number;
  specialite?: string;
  created_at?: string;
  [key: string]: unknown;
}

type TabType = 'infos' | 'email' | 'telephone' | 'securite';

export default function ProfilPage() {
  const { user, role, userRoles, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [profileTable, setProfileTable] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [sessionExpired, setSessionExpired] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('infos');

  // Info editing
  const [editNom, setEditNom] = useState('');
  const [editPrenom, setEditPrenom] = useState('');
  const [editDateNaissance, setEditDateNaissance] = useState('');
  const [editSexe, setEditSexe] = useState('');
  const [savingInfo, setSavingInfo] = useState(false);
  const [infoSuccess, setInfoSuccess] = useState('');

  // Email verification
  const [newEmail, setNewEmail] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [emailStep, setEmailStep] = useState<'idle' | 'code-sent' | 'verified'>('idle');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState('');

  // Phone verification
  const [newPhone, setNewPhone] = useState('');
  const [phoneCode, setPhoneCode] = useState('');
  const [phoneStep, setPhoneStep] = useState<'idle' | 'code-sent' | 'verified'>('idle');
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [phoneSuccess, setPhoneSuccess] = useState('');

  // Name change tracking
  const [nameChangesLeft, setNameChangesLeft] = useState(1);

  // Date naissance deadline
  const [naissanceWarning, setNaissanceWarning] = useState(false);
  const [naissanceUrgent, setNaissanceUrgent] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login', { replace: true }); return; }
    if (!role || userRoles.length === 0) return; // Attendre que les rôles soient chargés
    loadProfile();
  }, [user, authLoading, role, userRoles]);

  const determineTable = (): string => {
    if (userRoles.includes('super_admin')) return 'super_admins';
    if (userRoles.includes('admin')) return 'super_admins';
    if (userRoles.includes('comptable')) return 'comptables';
    if (userRoles.includes('medecin')) return 'medecins';
    if (userRoles.includes('patient')) return 'patients';
    if (userRoles.includes('secretaire')) return 'secretaires';
    if (userRoles.includes('laborantin')) return 'laborantins';
    if (userRoles.includes('sage_femme')) return 'sage_femmes';
    if (userRoles.includes('infirmier')) return 'infirmiers';
    return '';
  };

  const loadProfile = async () => {
    if (!user) return;
    try {
      setLoading(true);
      setSessionExpired(false);
      
      // Vérifier d'abord que la session est toujours valide
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        setSessionExpired(true);
        setErrorMsg('Votre session a expiré. Veuillez vous reconnecter.');
        return;
      }
      
      const table = determineTable();
      if (!table) { setErrorMsg('Profil non trouvé'); return; }
      setProfileTable(table);

      const { data, error } = await supabase.from(table).select('*').eq('user_id', user.id).maybeSingle();
      
      if (error) {
        // Détecter les erreurs d'authentification
        if (error.code === 'PGRST301' || error.message?.includes('JWT') || error.message?.includes('token') || error.message?.includes('expired')) {
          setSessionExpired(true);
          setErrorMsg('Votre session a expiré. Veuillez vous reconnecter.');
        } else {
          setErrorMsg('Erreur lors du chargement du profil');
        }
        return;
      }
      
      if (!data) { setErrorMsg('Profil non trouvé'); return; }

      setProfile(data as ProfileData);
      setEditNom(data.nom || '');
      setEditPrenom(data.prenom || '');
      setEditDateNaissance(data.date_naissance || '');
      setEditSexe(data.sexe || '');

      // Name changes tracking
      const changes = data.name_changes ?? 0;
      setNameChangesLeft(Math.max(0, 1 - changes));

      // Date naissance warning (patients only)
      if (role === 'patient' && !data.date_naissance && data.created_at) {
        const createdAt = new Date(data.created_at);
        const deadline = new Date(createdAt);
        deadline.setMonth(deadline.getMonth() + 1);
        const now = new Date();

        if (now < deadline) {
          setNaissanceWarning(true);
          const daysLeft = Math.ceil((deadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
          if (daysLeft <= 7) setNaissanceUrgent(true);
        }
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'Erreur inattendue');
      if (err.message?.includes('JWT') || err.message?.includes('token') || err.message?.includes('expired')) {
        setSessionExpired(true);
      }
    } finally {
      setLoading(false);
    }
  };

  // ============== UPDATE INFO ==============
  const handleSaveInfo = async () => {
    if (!profile || !user) return;
    setSavingInfo(true);
    setInfoSuccess('');
    setErrorMsg('');

    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      if (!token) throw new Error('Session expirée');

      const updates: Record<string, unknown> = {};
      if (editNom !== profile.nom) updates.nom = editNom;
      if (editPrenom !== profile.prenom) updates.prenom = editPrenom;
      if (role === 'patient' && editDateNaissance !== (profile.date_naissance || '')) {
        updates.date_naissance = editDateNaissance || null;
      }
      if (editSexe !== (profile.sexe || '')) {
        updates.sexe = editSexe || null;
      }

      if (Object.keys(updates).length === 0) {
        setErrorMsg('Aucune modification détectée');
        return;
      }

      const res = await supabase.functions.invoke('update-user-profile', {
        body: { action: 'update_info', ...updates },
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.error || res.data?.error) throw new Error(res.data?.error || 'Erreur lors de la mise à jour');

      setInfoSuccess('Profil mis à jour avec succès !');
      await loadProfile();
      setTimeout(() => setInfoSuccess(''), 4000);
    } catch (err: any) {
      setErrorMsg(err.message);
    } finally {
      setSavingInfo(false);
    }
  };

  // ============== EMAIL VERIFICATION ==============
  const handleSendEmailCode = async () => {
    if (!newEmail.trim()) { setEmailError('Veuillez saisir un email'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail.trim())) { setEmailError('Format d\'email invalide'); return; }
    if (newEmail.trim() === profile?.email) { setEmailError('C\'est déjà votre email actuel'); return; }

    setEmailLoading(true);
    setEmailError('');

    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      if (!token) throw new Error('Session expirée');

      const res = await supabase.functions.invoke('update-user-profile', {
        body: { action: 'send_verification_code', identifier: newEmail.trim(), type: 'email' },
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.error || res.data?.error) throw new Error(res.data?.error || 'Erreur envoi code');

      setEmailStep('code-sent');
    } catch (err: any) {
      setEmailError(err.message);
    } finally {
      setEmailLoading(false);
    }
  };

  const handleVerifyEmailCode = async () => {
    if (!emailCode.trim()) { setEmailError('Veuillez saisir le code'); return; }

    setEmailLoading(true);
    setEmailError('');

    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      if (!token) throw new Error('Session expirée');

      const res = await supabase.functions.invoke('update-user-profile', {
        body: { action: 'verify_contact_and_update', identifier: newEmail.trim(), type: 'email', code: emailCode.trim() },
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.error || res.data?.error) throw new Error(res.data?.error || 'Code invalide');

      setEmailStep('verified');
      setEmailSuccess('Email mis à jour avec succès !');
      await loadProfile();
      setTimeout(() => {
        setEmailSuccess('');
        setEmailStep('idle');
        setNewEmail('');
        setEmailCode('');
      }, 5000);
    } catch (err: any) {
      setEmailError(err.message);
    } finally {
      setEmailLoading(false);
    }
  };

  const resetEmailFlow = () => {
    setEmailStep('idle');
    setNewEmail('');
    setEmailCode('');
    setEmailError('');
  };

  // ============== PHONE VERIFICATION ==============
  const handleSendPhoneCode = async () => {
    if (!newPhone.trim()) { setPhoneError('Veuillez saisir un numéro'); return; }
    if (newPhone.trim() === profile?.telephone) { setPhoneError('C\'est déjà votre numéro actuel'); return; }

    setPhoneLoading(true);
    setPhoneError('');

    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      if (!token) throw new Error('Session expirée');

      // Normalize phone
      let normalized = newPhone.trim().replace(/\s+/g, '');
      if (!normalized.startsWith('+')) {
        if (normalized.startsWith('0')) normalized = '+229' + normalized.substring(1);
        else if (normalized.match(/^\d+$/)) normalized = '+229' + normalized;
      }

      const res = await supabase.functions.invoke('update-user-profile', {
        body: { action: 'send_verification_code', identifier: normalized, type: 'phone' },
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.error || res.data?.error) throw new Error(res.data?.error || 'Erreur envoi code');

      setPhoneStep('code-sent');
    } catch (err: any) {
      setPhoneError(err.message);
    } finally {
      setPhoneLoading(false);
    }
  };

  const handleVerifyPhoneCode = async () => {
    if (!phoneCode.trim()) { setPhoneError('Veuillez saisir le code'); return; }

    setPhoneLoading(true);
    setPhoneError('');

    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      if (!token) throw new Error('Session expirée');

      let normalized = newPhone.trim().replace(/\s+/g, '');
      if (!normalized.startsWith('+')) {
        if (normalized.startsWith('0')) normalized = '+229' + normalized.substring(1);
        else if (normalized.match(/^\d+$/)) normalized = '+229' + normalized;
      }

      const res = await supabase.functions.invoke('update-user-profile', {
        body: { action: 'verify_contact_and_update', identifier: normalized, type: 'phone', code: phoneCode.trim() },
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.error || res.data?.error) throw new Error(res.data?.error || 'Code invalide');

      setPhoneStep('verified');
      setPhoneSuccess('Téléphone mis à jour avec succès !');
      await loadProfile();
      setTimeout(() => {
        setPhoneSuccess('');
        setPhoneStep('idle');
        setNewPhone('');
        setPhoneCode('');
      }, 5000);
    } catch (err: any) {
      setPhoneError(err.message);
    } finally {
      setPhoneLoading(false);
    }
  };

  const resetPhoneFlow = () => {
    setPhoneStep('idle');
    setNewPhone('');
    setPhoneCode('');
    setPhoneError('');
  };

  const formatDate = (d: string) => {
    if (!d) return 'Non renseignée';
    return new Date(d + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  if (loading || authLoading || !role) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-clinic-blue animate-spin mb-4 block"></i>
          <span className="text-clinic-blue-dark text-xl">Chargement du profil...</span>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          {sessionExpired ? (
            <>
              <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-timer-line text-4xl text-amber-600"></i>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-3">Session expirée</h1>
              <p className="text-gray-600 mb-8">
                Votre session a expiré après une période d'inactivité. 
                Reconnectez-vous pour accéder à votre profil et continuer votre travail.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button onClick={() => navigate('/login')} className="px-6 py-3 bg-clinic-blue text-white rounded-full hover:bg-clinic-blue-dark transition-all font-semibold whitespace-nowrap cursor-pointer">
                  <i className="ri-login-box-line mr-2"></i>Se reconnecter
                </button>
                <button onClick={() => navigate('/')} className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 transition-all font-semibold whitespace-nowrap cursor-pointer">
                  <i className="ri-home-line mr-2"></i>Retour à l'accueil
                </button>
              </div>
            </>
          ) : (
            <>
              <i className="ri-error-warning-line text-5xl text-red-500 mb-4 block"></i>
              <p className="text-xl text-gray-700 mb-4">{errorMsg || 'Profil introuvable'}</p>
              <button onClick={() => navigate('/')} className="px-6 py-2 bg-clinic-blue text-white rounded-full hover:bg-clinic-blue-dark transition-all font-semibold whitespace-nowrap cursor-pointer">
                Retour à l'accueil
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  const roleLabel = (r: string) => {
    switch (r) {
      case 'super_admin': return 'Administrateur';
      case 'medecin': return 'Médecin';
      case 'patient': return 'Patient';
      case 'secretaire': return 'Secrétaire';
      case 'laborantin': return 'Biologiste médical';
      case 'sage_femme': return 'Sage-femme';
      default: return r;
    }
  };

  return (
    <div className="min-h-[100dvh] bg-white flex flex-col">
      <Header />

      <main className="flex-1 py-6 sm:py-10 md:py-12 px-3 sm:px-4 md:px-6 lg:px-8 min-w-0">
        <div className="max-w-4xl mx-auto">

          {/* En-tête profil */}
          <div className="mb-6 sm:mb-10">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 mb-3">
              <div className="w-16 h-16 bg-clinic-blue/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                <i className="ri-user-3-line text-3xl text-clinic-blue"></i>
              </div>
              <div className="text-center sm:text-left">
                <h1 className="text-2xl sm:text-3xl font-bold text-clinic-blue-dark">Mon profil</h1>
                <p className="text-sm text-gray-600">{role && roleLabel(role)} — Membre depuis {profile.created_at ? formatDate(profile.created_at.split('T')[0]) : '...'}</p>
              </div>
            </div>
          </div>

          {/* Alerte date de naissance */}
          {naissanceWarning && (
            <div className={`mb-6 p-5 rounded-2xl border-2 flex items-start gap-4 ${naissanceUrgent ? 'bg-red-50 border-red-300' : 'bg-amber-50 border-amber-300'}`}>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${naissanceUrgent ? 'bg-red-500' : 'bg-amber-500'}`}>
                <i className={`${naissanceUrgent ? 'ri-alert-fill' : 'ri-timer-fill'} text-xl text-white`}></i>
              </div>
              <div className="flex-1">
                <h3 className={`font-bold ${naissanceUrgent ? 'text-red-700' : 'text-amber-700'}`}>
                  {naissanceUrgent ? '⚠️ Action urgente requise' : 'Information manquante'}
                </h3>
                <p className={`text-sm mt-1 ${naissanceUrgent ? 'text-red-600' : 'text-amber-600'}`}>
                  Votre date de naissance n'est pas renseignée. Vous devez la compléter dans le mois suivant votre inscription. 
                  Sans cela, certaines fonctionnalités pourraient être limitées.
                </p>
                <button
                  onClick={() => { setActiveTab('infos'); document.getElementById('date-naissance-field')?.focus(); }}
                  className={`mt-3 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all ${naissanceUrgent ? 'bg-red-500 text-white hover:bg-red-600' : 'bg-amber-500 text-white hover:bg-amber-600'}`}
                >
                  <i className="ri-edit-line mr-1"></i> Compléter ma date de naissance
                </button>
              </div>
            </div>
          )}

          {/* Onglets - grille 2x2 sur mobile pour eviter debordement */}
          <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-1 bg-gray-100 p-1 rounded-2xl mb-8 min-w-0">
            {(['infos', 'email', 'telephone', 'securite'] as TabType[]).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-2 sm:px-3 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-semibold cursor-pointer transition-all text-center min-w-0 ${activeTab === tab ? 'bg-white text-clinic-blue-dark shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <i className={`${tab === 'infos' ? 'ri-user-line' : tab === 'email' ? 'ri-mail-line' : tab === 'telephone' ? 'ri-phone-line' : 'ri-shield-check-line'} mr-1 sm:mr-1.5`}></i>
                <span className="hidden sm:inline">{tab === 'infos' ? 'Infos personnelles' : tab === 'email' ? 'Email' : tab === 'telephone' ? 'Téléphone' : 'Sécurité'}</span>
                <span className="sm:hidden">{tab === 'infos' ? 'Infos' : tab === 'email' ? 'Email' : tab === 'telephone' ? 'Tél.' : 'Sécu.'}</span>
              </button>
            ))}
          </div>

          {/* Messages globaux */}
          {errorMsg && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
              <i className="ri-error-warning-line text-red-500 text-xl"></i>
              <p className="text-red-700 text-sm">{errorMsg}</p>
              <button onClick={() => setErrorMsg('')} className="ml-auto text-red-400 hover:text-red-600 cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>
          )}

          {/* === TAB: INFOS PERSONNELLES === */}
          {activeTab === 'infos' && (
            <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8">
              <h2 className="text-xl font-bold text-clinic-blue-dark mb-6 flex items-center gap-2">
                <i className="ri-user-line text-clinic-blue"></i> Informations personnelles
              </h2>

              <div className="space-y-5">
                {/* Nom */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Nom {nameChangesLeft === 0 && <span className="text-amber-600 text-xs font-normal">(déjà modifié une fois)</span>}
                  </label>
                  <input
                    type="text"
                    value={editNom}
                    onChange={(e) => setEditNom(e.target.value)}
                    disabled={nameChangesLeft === 0}
                    className={`w-full px-4 py-3 rounded-xl border text-sm transition-all ${nameChangesLeft === 0 ? 'bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed' : 'border-gray-300 focus:border-clinic-blue focus:ring-2 focus:ring-clinic-blue/20 outline-none'}`}
                  />
                  {nameChangesLeft === 0 && (
                    <p className="text-xs text-amber-600 mt-1">Le nom ne peut être modifié qu'une seule fois. Contactez un administrateur si nécessaire.</p>
                  )}
                </div>

                {/* Prénom */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Prénom {nameChangesLeft === 0 && <span className="text-amber-600 text-xs font-normal">(déjà modifié une fois)</span>}
                  </label>
                  <input
                    type="text"
                    value={editPrenom}
                    onChange={(e) => setEditPrenom(e.target.value)}
                    disabled={nameChangesLeft === 0}
                    className={`w-full px-4 py-3 rounded-xl border text-sm transition-all ${nameChangesLeft === 0 ? 'bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed' : 'border-gray-300 focus:border-clinic-blue focus:ring-2 focus:ring-clinic-blue/20 outline-none'}`}
                  />
                </div>

                {/* Date de naissance (patients only) */}
                {role === 'patient' && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2" htmlFor="date-naissance-field">
                      Date de naissance {!profile.date_naissance && <span className="text-red-500 text-xs">(requise)</span>}
                    </label>
                    <input
                      id="date-naissance-field"
                      type="date"
                      value={editDateNaissance}
                      onChange={(e) => setEditDateNaissance(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 text-sm focus:border-clinic-blue focus:ring-2 focus:ring-clinic-blue/20 outline-none transition-all"
                    />
                    {profile.date_naissance && (
                      <p className="text-xs text-gray-500 mt-1">Actuelle : {formatDate(profile.date_naissance)}</p>
                    )}
                  </div>
                )}

                {/* Sexe - tous les rôles */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Sexe</label>
                  <select
                    value={editSexe}
                    onChange={(e) => setEditSexe(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 text-sm focus:border-clinic-blue focus:ring-2 focus:ring-clinic-blue/20 outline-none transition-all pr-8"
                  >
                    <option value="">Non renseigné</option>
                    <option value="Masculin">Masculin</option>
                    <option value="Féminin">Féminin</option>
                  </select>
                </div>

                {/* Spécialité (medecins only) */}
                {role === 'medecin' && profile.specialite && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Spécialité</label>
                    <input
                      type="text"
                      value={profile.specialite}
                      disabled
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-600 text-sm cursor-not-allowed"
                    />
                    <p className="text-xs text-gray-400 mt-1">La spécialité ne peut être modifiée que par un administrateur</p>
                  </div>
                )}

                {/* Bouton sauvegarder */}
                <div className="pt-4 border-t border-gray-100">
                  {infoSuccess && (
                    <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-xl flex items-center gap-2">
                      <i className="ri-checkbox-circle-fill text-green-600"></i>
                      <span className="text-green-700 text-sm">{infoSuccess}</span>
                    </div>
                  )}
                  <button
                    onClick={handleSaveInfo}
                    disabled={savingInfo || (nameChangesLeft === 0 && editNom === profile.nom && editPrenom === profile.prenom && editSexe === (profile.sexe || ''))}
                    className="px-6 py-3 bg-clinic-blue text-white rounded-full hover:bg-clinic-blue-dark transition-all font-semibold whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {savingInfo ? (
                      <><i className="ri-loader-4-line animate-spin mr-2"></i>Sauvegarde...</>
                    ) : (
                      <><i className="ri-save-line mr-2"></i>Enregistrer les modifications</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* === TAB: EMAIL === */}
          {activeTab === 'email' && (
            <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8">
              <h2 className="text-xl font-bold text-clinic-blue-dark mb-6 flex items-center gap-2">
                <i className="ri-mail-line text-clinic-blue"></i> Adresse email
              </h2>

              {/* Email actuel */}
              <div className="mb-8 p-4 bg-gray-50 rounded-xl border border-gray-200">
                <p className="text-sm text-gray-500 mb-1">Email actuel</p>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-base font-semibold text-clinic-blue-dark truncate min-w-0">
                    {profile.email || (user?.email && !user.email.includes('@cliniquesaintefamille.local') ? user.email : null) || 'Non renseigné'}
                  </span>
                  {(profile.email_verified_at || (user?.email && !user.email.includes('@cliniquesaintefamille.local'))) && (
                    <span className="shrink-0 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-normal">Vérifié</span>
                  )}
                </div>
              </div>

              {/* Success */}
              {emailSuccess && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
                  <i className="ri-checkbox-circle-fill text-green-600 text-xl"></i>
                  <span className="text-green-700 text-sm">{emailSuccess}</span>
                </div>
              )}

              {/* Error */}
              {emailError && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
                  <i className="ri-error-warning-line text-red-500 text-xl"></i>
                  <span className="text-red-700 text-sm">{emailError}</span>
                  <button onClick={() => setEmailError('')} className="ml-auto text-red-400 cursor-pointer"><i className="ri-close-line"></i></button>
                </div>
              )}

              {emailStep !== 'verified' && (
                <>
                  {/* Nouvel email */}
                  <div className="mb-5">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Nouvel email</label>
                    <input
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      disabled={emailStep === 'code-sent'}
                      placeholder="nouveau@email.com"
                      className={`w-full px-4 py-3 rounded-xl border text-sm transition-all ${emailStep === 'code-sent' ? 'bg-gray-100 border-gray-200' : 'border-gray-300 focus:border-clinic-blue focus:ring-2 focus:ring-clinic-blue/20 outline-none'}`}
                    />
                  </div>

                  {/* Code verification */}
                  {emailStep === 'code-sent' && (
                    <div className="mb-5">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Code de vérification</label>
                      <div className="flex gap-3">
                        <input
                          type="text"
                          value={emailCode}
                          onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="123456"
                          maxLength={6}
                          className="flex-1 px-4 py-3 rounded-xl border border-gray-300 text-sm text-center tracking-[0.5em] font-mono focus:border-clinic-blue focus:ring-2 focus:ring-clinic-blue/20 outline-none transition-all"
                        />
                        <button
                          onClick={handleVerifyEmailCode}
                          disabled={emailLoading || emailCode.length !== 6}
                          className="px-6 py-3 bg-clinic-green text-white rounded-full hover:bg-clinic-green/90 transition-all font-semibold whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {emailLoading ? <i className="ri-loader-4-line animate-spin"></i> : 'Vérifier'}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Code envoyé à <strong>{newEmail.trim()}</strong>. Valable 10 minutes.
                      </p>
                    </div>
                  )}

                  {/* Boutons */}
                  <div className="flex gap-3 pt-2">
                    {emailStep === 'idle' ? (
                      <button
                        onClick={handleSendEmailCode}
                        disabled={emailLoading || !newEmail.trim()}
                        className="px-6 py-3 bg-clinic-blue text-white rounded-full hover:bg-clinic-blue-dark transition-all font-semibold whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {emailLoading ? <><i className="ri-loader-4-line animate-spin mr-2"></i>Envoi...</> : <><i className="ri-send-plane-line mr-2"></i>Envoyer le code de vérification</>}
                      </button>
                    ) : (
                      <button
                        onClick={resetEmailFlow}
                        className="px-5 py-2.5 border border-gray-300 text-gray-600 rounded-full hover:bg-gray-50 transition-all text-sm font-medium whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-arrow-left-line mr-1"></i> Changer d'email
                      </button>
                    )}
                  </div>

                  {emailStep === 'idle' && (
                    <div className="mt-5 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                      <p className="text-sm text-blue-700 flex items-start gap-2">
                        <i className="ri-information-line flex-shrink-0 mt-0.5"></i>
                        Un code de vérification sera envoyé à votre nouvelle adresse email pour confirmer qu'elle vous appartient.
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* === TAB: TELEPHONE === */}
          {activeTab === 'telephone' && (
            <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8">
              <h2 className="text-xl font-bold text-clinic-blue-dark mb-6 flex items-center gap-2">
                <i className="ri-phone-line text-clinic-blue"></i> Numéro de téléphone
              </h2>

              {/* Téléphone actuel */}
              <div className="mb-8 p-4 bg-gray-50 rounded-xl border border-gray-200">
                <p className="text-sm text-gray-500 mb-1">Numéro actuel</p>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-base font-semibold text-clinic-blue-dark truncate min-w-0">
                    {profile.telephone || user?.user_metadata?.phone || 'Non renseigné'}
                  </span>
                  {(profile.phone_verified_at || user?.user_metadata?.phone) && (
                    <span className="shrink-0 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-normal">Vérifié</span>
                  )}
                </div>
              </div>

              {phoneSuccess && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
                  <i className="ri-checkbox-circle-fill text-green-600 text-xl"></i>
                  <span className="text-green-700 text-sm">{phoneSuccess}</span>
                </div>
              )}

              {phoneError && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
                  <i className="ri-error-warning-line text-red-500 text-xl"></i>
                  <span className="text-red-700 text-sm">{phoneError}</span>
                  <button onClick={() => setPhoneError('')} className="ml-auto text-red-400 cursor-pointer"><i className="ri-close-line"></i></button>
                </div>
              )}

              {phoneStep !== 'verified' && (
                <>
                  <div className="mb-5">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Nouveau numéro (format international)</label>
                    <input
                      type="tel"
                      value={newPhone}
                      onChange={(e) => setNewPhone(e.target.value)}
                      disabled={phoneStep === 'code-sent'}
                      placeholder="+229 XX XX XX XX"
                      className={`w-full px-4 py-3 rounded-xl border text-sm transition-all ${phoneStep === 'code-sent' ? 'bg-gray-100 border-gray-200' : 'border-gray-300 focus:border-clinic-blue focus:ring-2 focus:ring-clinic-blue/20 outline-none'}`}
                    />
                  </div>

                  {phoneStep === 'code-sent' && (
                    <div className="mb-5">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Code reçu par SMS</label>
                      <div className="flex gap-3">
                        <input
                          type="text"
                          value={phoneCode}
                          onChange={(e) => setPhoneCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="123456"
                          maxLength={6}
                          className="flex-1 px-4 py-3 rounded-xl border border-gray-300 text-sm text-center tracking-[0.5em] font-mono focus:border-clinic-blue focus:ring-2 focus:ring-clinic-blue/20 outline-none transition-all"
                        />
                        <button
                          onClick={handleVerifyPhoneCode}
                          disabled={phoneLoading || phoneCode.length !== 6}
                          className="px-6 py-3 bg-clinic-green text-white rounded-full hover:bg-clinic-green/90 transition-all font-semibold whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {phoneLoading ? <i className="ri-loader-4-line animate-spin"></i> : 'Vérifier'}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Code envoyé par SMS. Valable 10 minutes.</p>
                    </div>
                  )}

                  <div className="flex gap-3 pt-2">
                    {phoneStep === 'idle' ? (
                      <button
                        onClick={handleSendPhoneCode}
                        disabled={phoneLoading || !newPhone.trim()}
                        className="px-6 py-3 bg-clinic-blue text-white rounded-full hover:bg-clinic-blue-dark transition-all font-semibold whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {phoneLoading ? <><i className="ri-loader-4-line animate-spin mr-2"></i>Envoi...</> : <><i className="ri-chat-smile-2-line mr-2"></i>Envoyer le code par SMS</>}
                      </button>
                    ) : (
                      <button
                        onClick={resetPhoneFlow}
                        className="px-5 py-2.5 border border-gray-300 text-gray-600 rounded-full hover:bg-gray-50 transition-all text-sm font-medium whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-arrow-left-line mr-1"></i> Changer de numéro
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* === TAB: SECURITE === */}
          {activeTab === 'securite' && (
            <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8">
              <h2 className="text-xl font-bold text-clinic-blue-dark mb-6 flex items-center gap-2">
                <i className="ri-shield-check-line text-clinic-blue"></i> Sécurité du compte
              </h2>

              <div className="space-y-6">
                <div className="p-5 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-clinic-blue-dark">Mot de passe</h3>
                      <p className="text-sm text-gray-500 mt-1">Modifiez votre mot de passe régulièrement pour sécuriser votre compte</p>
                    </div>
                    <button
                      onClick={() => navigate('/change-password')}
                      className="px-5 py-2.5 bg-clinic-blue text-white rounded-full hover:bg-clinic-blue-dark transition-all font-semibold text-sm whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-lock-line mr-2"></i>Modifier
                    </button>
                  </div>
                </div>

                <div className="p-5 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-clinic-blue-dark">Déconnexion de tous les appareils</h3>
                      <p className="text-sm text-gray-500 mt-1">Déconnectez-vous de tous les appareils où votre compte est actif. Cette action invalide toutes vos sessions en cours.</p>
                    </div>
                    <button
                      onClick={async () => {
                        if (!window.confirm('Êtes-vous sûr de vouloir vous déconnecter de TOUS vos appareils ? Toutes vos sessions seront fermées.')) return;
                        try {
                          await logAccessServer({
                            userId: user.id,
                            userRole: role || 'unknown',
                            actionType: 'LOGOUT',
                            resourceType: 'SESSION',
                            details: { operation: 'Déconnexion globale (tous les appareils)', userRoles: userRoles },
                          });
                        } catch { /* silencieux */ }
                        await supabase.auth.signOut({ scope: 'global' });
                        navigate('/login', { replace: true });
                      }}
                      className="px-5 py-2.5 border-2 border-red-400 text-red-500 rounded-full hover:bg-red-50 transition-all font-semibold text-sm whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-logout-box-r-line mr-2"></i>Déconnecter partout
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </main>

      <Footer />
    </div>
  );
}